/**
 * Dropzone: pick a file by button or by dropping it. The one ingest
 * trigger's face; the panel behind it owns what happens to the file.
 */
import type { ReactNode } from 'react';
import { DropZone, FileTrigger, isFileDropItem } from 'react-aria-components';
import { BRAND, RADIUS, SPACE, TEXT, WASH_ALPHA, tint, usePalette } from '../theme';
import { ActionButton } from './action-button';

export function Dropzone({
  label,
  hint,
  accept,
  busy = false,
  onFile,
  onRefused,
  children,
}: {
  /** The button's verb ("Upload IFC File"). */
  label: ReactNode;
  /** One line under the button (the sniff's schema and entity count). */
  hint?: ReactNode;
  /** Accepted extensions or MIME types (".ifc"). */
  accept: readonly string[];
  busy?: boolean;
  onFile: (file: File) => void;
  /** The browser refused to hand the dropped item over as a File; the panel says so. */
  onRefused: (reason: string) => void;
  children?: ReactNode;
}) {
  const p = usePalette();
  return (
    <DropZone
      getDropOperation={(types) => (accept.some((a) => types.has(a)) ? 'copy' : 'cancel')}
      onDrop={(e) => {
        const item = e.items.find(isFileDropItem);
        if (item) {
          item.getFile().then(onFile, (error: unknown) => {
            onRefused(error instanceof Error ? error.message : String(error));
          });
        }
      }}
      style={({ isDropTarget }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: SPACE.sm,
        padding: SPACE.md,
        borderRadius: RADIUS.md,
        border: `1px dashed ${isDropTarget ? BRAND.teal : p.hairline}`,
        background: isDropTarget ? tint(BRAND.teal, WASH_ALPHA.fill) : 'transparent',
        outline: 'none',
      })}
    >
      <FileTrigger
        acceptedFileTypes={[...accept]}
        onSelect={(files) => {
          const file = files?.[0];
          if (file) onFile(file);
        }}
      >
        <ActionButton variant="brand" isDisabled={busy}>
          {label}
        </ActionButton>
      </FileTrigger>
      {hint && <span style={{ fontSize: TEXT.micro, color: p.inkSecondary }}>{hint}</span>}
      {children}
    </DropZone>
  );
}
